// REACT IMPORTS
import React, { useEffect, useMemo, useRef, useState } from "react";
import Drawer from "components/ui-component/SideDrawer";
import Skeleton from "react-loading-skeleton";

// COMPONENT IMPORTS
import { useAppDispatch } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { Menu } from "@headlessui/react";
import {
  dontCancelPlan,
  subscriptionCountGet,
  subscriptionHistoryGet,
  useSubscriptionCount,
  useSubscriptionHistory,
} from "store/slices/subscriptionSlice";
import SelectAllDropDown from "components/form/SelectAllDropDown";
import CustomTable from "components/ui-component/CustomTable";
import {
  FilterAmountOptions,
  FilterDateOptions,
  PaymentFilterMethodOptions,
  SubscriptionsStatusOptions,
} from "utils/Options";
import {
  cardToShortname,
  changePaymentScheduleName,
  encode,
  formatCurrency,
  getBrowserTimezoneName,
  getBrowserTimezoneOffset,
  getPaymentMethod,
} from "utils/HelperFunctions";
import Header from "components/layout/header";
import AmountFilter from "components/payments/AmountFilter";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";
import { isSettlementOriginPlan } from "utils/settlementPlan";

// ICONS IMPORTS
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { MoreIcon } from "icons/payments/MoreIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { CheckCircleIcon } from "icons/subscription/CheckCircleIcon";
import { CloseSmRedIcon } from "icons/subscription/CloseSmRedIcon";
import { ReceiptEditIcon } from "icons/subscription/ReceiptEditIcon";
import { CloseRingIcon } from "icons/subscription/CloseRingIcon";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import DateFilter from "components/subscriptions/DateFilter";
import CustomModal from "components/ui-component/CustomModal";
import { cancelPaymentPlan } from "store/slices/paymentsSlice";
import UpdatePaymentPlan from "components/dashboard/UpdatePaymentPlan";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "components/form/Button";
import NoSubscription from "icons/subscription/NoSubscription";
import { formatPaymentListDate } from "utils/DateFormat";
import CancelSubscribePlan from "components/subscribe/CancelSubscribePlan";
import UpdateSusbcribePlan from "components/subscribe/UpdateSusbcribePlan";
import CancelledCalendar from "icons/subscription/CancelledCalendar";
import { decode } from "utils/HelperFunctions";
import { useMediaQuery } from "react-responsive";
import { SettingsValue } from "types/payments";
import type {
  StatusType,
  CancelProps,
  FilterType,
  SubscriptionAndPlansColumn,
  SubscriptionPlansRow,
} from "../../types/subscriptionAndPlans";
import { RootState } from "store/store";
import { ToastState } from "types/common";
import MessageToast from "components/ui-component/MessageToast";
import PaymentMethodUpdateIcon from "icons/subscription/PaymentMethodUpdateIcon";
import UpdatePaymentMethod from "components/subscribe/UpdatePaymentMethod";
import ManageUpdateIcon from "icons/subscription/ManageUpdateIcon";
import ScheduleUpdatePlan from "components/subscribe/ScheduleUpdatePlan";
import { isCancelsSoon } from "utils/Formater/DateFormat";
import PlanPreview from "components/dashboard/PlanPreview";
import ExcalimIcon from "icons/payments/ExclaimIcon";
import { RedCrossIcon } from "icons/payments/RedCrossIcon";
import { ClockYellowIcon } from "icons/payments/ClockYellowIcon";
import { useLeadData } from "store/slices/dashboardSlice";
import { TestIdScope, useTid } from "utils/testid";

/* ------------------------------------- SUBSCRIPTION AND PLAN ------------------------------------- */

const getSubscriptionName = (productDetails: any) => {
  if (productDetails?.length > 0) {
    const sortedProducts = [...productDetails].filter(
      (x: any) => x?.priceDetails?.frequency === "recurring"
    );

    let title = sortedProducts[0]?.productDetails?.name || "";
    if (sortedProducts.length > 1) {
      return (
        title?.charAt(0) +
        title?.slice(1) +
        ` and ${sortedProducts.length - 1} more`
      );
    } else {
      return title?.charAt(0) + title?.slice(1);
    }
  } else {
    return "Custom Plan";
  }
};

const getStatusDetails = (status: string) => {
  switch (status) {
    case "active":
      return {
        label: "Active",
        Icon: <CheckCircleIcon />,
        className: "bg-[#1678F20D] text-blue text-sm",
      };
    case "cancelled":
    case "canceled":
      return {
        label: "Canceled",
        Icon: <CloseSmRedIcon />,
        className: "bg-[#FF00000D] text-red text-sm",
      };
    case "completed":
    case "ended":
      return {
        label: "Completed",
        Icon: <GreenTrueIcon />,
        className: "bg-[#23B3390D] text-green text-sm",
      };
    case "incomplete":
      return {
        label: "Incomplete",
        Icon: <ExcalimIcon />,
        className: "bg-[#4F576A0D] text-txtSecondary text-sm",
      };
    case "unpaid":
      return {
        label: "Unpaid",
        Icon: <RedCrossIcon />,
        className: "bg-[#FF00000D] text-red text-sm",
      };
    case "past_due":
      return {
        label: "Past Due",
        Icon: <ClockYellowIcon />,
        className: "bg-[#FDB6000D] text-yellow text-sm",
      };
    default:
      return {
        label: "",
        Icon: <></>,
        className: "",
      };
  }
};

const SubscriptionsAndPlansContent = () => {
  const tid = useTid();
  const dispatch = useAppDispatch();
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  const isLessThan920 = useMediaQuery({ query: "(max-width:920px)" });
  const isLessThan680 = useMediaQuery({ query: "(max-width:680px)" });

  const filterScrollRef = useRef<HTMLDivElement>(null);
  const amountFilterRef = useRef<HTMLDivElement>(null);
  const dateFilterRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const methodFilterRef = useRef<HTMLDivElement>(null);

  const userData = useUserData();
  const accountLeadData = useLeadData();
  const subscriptionCount = useSubscriptionCount();
  const subscriptionHistory = useSubscriptionHistory();
  const location = useLocation();
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const { accountPermission } = useSelector((state: RootState) => state.auth);

  // State
  const [selectedFilter, setSelectedFilter] = useState<StatusType>(
    location.state && location.state.status ? "active" : "all"
  );
  const [status, setStatus] = useState<any>(
    location.state && location.state.status ? ["Active"] : []
  );
  const [method, setMethod] = useState<any[]>([]);
  const [amount, setAmount] = useState<any>(undefined);
  const [dateInput1, setDateInput1] = useState<any>(undefined);
  const [dateInput2, setDateInput2] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<string>("");
  const [isNestedPreviewOpen, setIsNestedPreviewOpen] =
    useState<boolean>(false);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [dateFilter, setdateFilter] = useState<any>();
  const [cancelBtnLoading, setCancelBtnLoading] = useState<boolean>(false);
  const [cancelPlan, setCancelPlan] = useState<CancelProps>({
    id: null,
    showModal: false,
  });
  const [dontCancelPlanModal, setDontCancelPlanModal] = useState<CancelProps>({
    id: null,
    showModal: false,
  });
  const [manageUpdatePlanModal, setManageUpdatePlanModal] =
    useState<CancelProps>({
      id: null,
      showModal: false,
    });
  const [isDontCancelLoading, setIsDontCancelLoading] =
    useState<boolean>(false);
  const [cancelSubscribePlan, setCancelSubscribePlan] =
    useState<boolean>(false);
  const [cancelSubscribePlanId, setCancelSubscribePlanId] = useState<
    string | null
  >();
  const [updateSubscribePlan, setUpdateSubscribePlan] =
    useState<boolean>(false);
  const [updateSubscribePlanId, setUpdateSubscribePlanId] = useState<
    string | null
  >();
  const [updatePlanId, setUpdatePlanId] = useState<string>("");

  const [updateSubscriptionMethod, setUpdateSubscriptionMethod] =
    useState<boolean>(false);
  const [updateSubscriptionMethodId, setUpdateSubscriptionMethodId] =
    useState<any>();

  const [isUpdatePlanOpen, setIsUpdatePlanOpen] = useState<boolean>(false);
  const [handleDrawerSize, setHandleDrawerSize] = useState<number>(0);
  const [updateData, setUpdateData] = useState<any>();
  const [isUpdatePreview, setIsUpdatePreview] = useState<boolean>(false);
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [isApiCall, setIsApiCall] = useState<boolean>(false);
  const [msg, setMsg] = useState<ToastState>({
    variant: "",
    content: null,
  });
  const [filter, setFilter] = useState<FilterType>({
    skip: 0,
    limit: 20,
    method: undefined,
    status: "",
    amount: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  const searchParams = new URLSearchParams(location.search);

  // API Calls
  const handleCancelPaymentPlan = async () => {
    setCancelBtnLoading(true);
    try {
      const payload = {
        id: cancelPlan.id,
        email: [userData?.data?.accountData?.email],
        reason: "",
        cluster_id: clusterId,
        lead_id: accountLeadData?.lead_id,
        accounts_id: userData?.data?.accountData?.id,
        agent: userData?.data?.accountData?.lead_owner,
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
      };
      let res: any = await dispatch(cancelPaymentPlan(payload)).unwrap();
      setMsg({
        variant: res.body.success ? "success" : "error",
        content: res.body.success
          ? "Plan canceled successfully"
          : "Plan cancellation failed",
      });
    } catch (error) {
      setMsg({
        variant: "error",
        content: "Plan cancellation failed. Please try again.",
      });
    } finally {
      onGetSubscriptionHistory();
      onGetSubscriptionCount();
      setCancelBtnLoading(false);
      setCancelPlan({ id: null, showModal: false });
    }
  };

  const handleDontCancelPaymentPlan = async () => {
    if (!dontCancelPlanModal.id) return;
    setIsDontCancelLoading(true);

    try {
      let payload = {
        subscription_id: dontCancelPlanModal.id,
      };

      let res: any = await dispatch(dontCancelPlan(payload)).unwrap();
      if (res?.body?.success) {
        setMsg({
          content: "Your subscription will remain active.",
          variant: "success",
        });
      }
    } catch (error) {
      console.log("handleDontCancelPaymentPlan error ======", error);
    } finally {
      setIsDontCancelLoading(false);
      onGetSubscriptionHistory();
      onGetSubscriptionCount();
      setDontCancelPlanModal({ id: null, showModal: false });
    }
  };

  const onGetSubscriptionCount = async () => {
    setIsLoading(true);
    try {
      await dispatch(
        subscriptionCountGet({ accounts_id: userData?.data?.accountData?.id })
      ).unwrap();
    } catch (error: any) {
      console.log("get count err: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onGetSubscriptionHistory = async () => {
    setIsTableLoading(true);
    try {
      await dispatch(
        subscriptionHistoryGet({
          ...filter,
          accounts_id: userData?.data?.accountData?.id,
        })
      ).unwrap();
    } catch (error: any) {
      console.log("get payment history err: -->", error?.message);
    } finally {
      setIsTableLoading(false);
    }
  };

  const columns: SubscriptionAndPlansColumn[] = [
    {
      id: "creation_date",
      name: "Created Date",
      minWidth: "120px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.updation_date ? (
              formatPaymentListDate(
                row?.updation_date,
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "plan",
      name: "Plan",
      minWidth: "200px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="font-medium text-txtPrimary leading-normal text-sm">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.plan_id > 0 ? (
              "Payment Agreement"
            ) : row?.recurring_type === "schedule" ? (
              "Custom Plan"
            ) : (
              getSubscriptionName(row?.productSubscriptions)
            )}
          </div>
        );
      },
    },
    {
      id: "payment_method",
      name: "Payment Method",
      minWidth: "160px",
      cell: (row: SubscriptionPlansRow) => {
        let payment_collection_type =
          row?.recurring_type === "schedule" || row?.plan_id > 0
            ? "MOF"
            : row?.payment_collection_type || "";
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : (
              getPaymentMethod({
                card_type: row?.cc_type,
                method: row?.payment_method,
                digits: row?.cc_4digit,
                account_number: row?.cc_4digit,
                className: "",
                isHidden: false,
                payment_collection_type: payment_collection_type,
                bank_name: row?.bank_name,
                rowId: row?.id,
              }) || "--"
            )}
          </div>
        );
      },
    },
    {
      id: "last_payment_status",
      name: "Status",
      minWidth: "160px",
      cell: (row: SubscriptionPlansRow) => {
        let status =
          row?.recurring_type !== "subscription"
            ? row?.last_payment_status
              ? row?.last_payment_status?.toLowerCase()
              : ""
            : row?.sub_status
              ? row?.sub_status?.toLowerCase()
              : "active";
        let { label, Icon, className } = getStatusDetails(status);

        const isCancelledAtPeriodEnd =
          row?.recurring_type === "subscription" &&
          row?.canceled_at !== null &&
          isCancelsSoon(row?.canceled_at);

        const isDowngradeSchedule =
          row?.recurring_type === "subscription" &&
          row?.downgrade_pending &&
          row?.sub_status !== "CANCELLED" &&
          row?.sub_status !== "CANCELED";

        const date =
          row?.canceled_at && isCancelledAtPeriodEnd
            ? formatPaymentListDate(
                row?.canceled_at,
                userData?.data?.accountData?.timezone,
                "long"
              )
            : isDowngradeSchedule
              ? formatPaymentListDate(
                  row?.next_billing_date,
                  userData?.data?.accountData?.timezone,
                  "long"
                )
              : "";

        const tooltipText =
          (isCancelledAtPeriodEnd
            ? "Your plan will be canceled on "
            : "A plan change is scheduled for ") + date;

        const isReasonAvailable =
          row?.cancelling_reason && row?.cancelling_reason !== "";
        return (
          <div className="relative">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : status !== "" ? (
              <div className="flex items-center gap-[3px]">
                {isReasonAvailable ? (
                  <CustomTooltip
                    maxwidth="264px"
                    place="top"
                    id={`status-${row.id}`}
                    content={`<div class="font-normal text-xs leading-[18px] w-[150px]" >Reason: ${row.cancelling_reason}. </div>`}
                  >
                    <div
                      id={`status-${row.id}`}
                      className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md ${className}`}
                    >
                      {Icon}
                      {label}
                    </div>
                  </CustomTooltip>
                ) : (
                  <div
                    className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md ${className}`}
                  >
                    {Icon}
                    {label}
                  </div>
                )}
                {row?.showIcon &&
                  row?.showIcon !== "false" &&
                  label !== "Canceled" && (
                    <CustomTooltip
                      maxwidth="264px"
                      place="top"
                      id={`Schedule-custom-${row.id}`}
                      content={`<div class="font-normal text-xs leading-[18px] w-[250px]" > ${row?.recurring_type === "subscription" ? "There are unpaid invoice(s) for this subscription." : "One or more of your recent payments have failed. Click here to view plan and retry."} </div>`}
                      stopPropagation={true}
                    >
                      <div
                        className="w-fit cursor-pointer relative"
                        id={`Schedule-custom-${row.id}`}
                      >
                        <InfoRedCircleIcon />
                      </div>
                    </CustomTooltip>
                  )}
                {(isCancelledAtPeriodEnd || isDowngradeSchedule) && (
                  <CustomTooltip
                    maxwidth="264px"
                    place="top"
                    id={`Schedule-calendar-${row.id}`}
                    content={`<div class="font-normal text-xs leading-[18px] w-[180px]" > ${tooltipText}. </div>`}
                  >
                    <div
                      className="w-fit cursor-pointer relative"
                      id={`Schedule-calendar-${row.id}`}
                    >
                      <CancelledCalendar />
                    </div>
                  </CustomTooltip>
                )}
              </div>
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "payment_schedule",
      name: "Frequency",
      minWidth: "110px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="text-txtPrimary">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : (
              changePaymentScheduleName(
                row?.payment_schedule,
                row?.productSubscriptions,
                row?.recurring_type
              )
            )}
          </div>
        );
      },
    },
    {
      id: "next_billing_date",
      name: "Next Payment",
      minWidth: "130px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="capitalize text-sm text-txtSecondary">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.sub_status === "CANCELLED" ||
              row?.sub_status === "ENDED" ||
              row?.last_payment_status === "Cancelled" ||
              row?.last_payment_status === "Completed" ? (
              "--"
            ) : row.next_billing_date ? (
              formatPaymentListDate(
                row.next_billing_date,
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "next_payment_amount",
      name: "Next Payment Amount",
      minWidth: "190px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="capitalize text-sm text-txtSecondary">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.sub_status === "CANCELLED" ||
              row?.sub_status === "ENDED" ||
              row?.last_payment_status === "Cancelled" ||
              row?.last_payment_status === "Completed" ? (
              "--"
            ) : row?.next_payment_amount ? (
              formatCurrency(
                row?.next_payment_amount + (row?.cf_amount || 0)
              ) || "$0.00"
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "id",
      name: "",
      right: "true",
      minWidth: "120px",
      cell: (row: SubscriptionPlansRow) => {
        const isAgreement = row?.plan_id && row?.plan_id > 0;

        const filteredProducts = row?.productSubscriptions?.filter(
          (product) => product?.priceDetails?.frequency === "recurring"
        );

        const isSettlementPlan = isSettlementOriginPlan(row);

        const isUpdatePlan = isSettlementPlan
          ? false
          : row?.plan_id > 0 || row?.recurring_type === "schedule"
            ? accountPermission?.modify_plan === SettingsValue.YES &&
              !isAgreement
            : filteredProducts?.length === 1 &&
              !row?.downgrade_pending &&
              ["ACTIVE", "PAST_DUE"].includes(row?.sub_status) &&
              row?.payment_collection_type === "MOF";

        const isUpdatePaymentMethod =
          (row?.recurring_type === "subscription" &&
            ["ACTIVE", "PAST_DUE", "UNPAID", "INCOMPLETE"].includes(
              row?.sub_status
            )) ||
          (row?.recurring_type === "schedule" &&
            row.last_payment_status?.toLowerCase() !== "cancelled" &&
            row?.last_payment_status?.toLowerCase() !== "completed");

        let id = `${row?.id}`;

        const isCancelledAtPeriodEnd =
          row?.recurring_type === "subscription" &&
          row?.canceled_at !== null &&
          isCancelsSoon(row?.canceled_at);

        const isDowngradeSchedule =
          row?.recurring_type === "subscription" &&
          row?.downgrade_pending &&
          row?.sub_status !== "CANCELLED" &&
          row?.sub_status !== "CANCELED";

        return isTableLoading ? (
          <></>
        ) : (
          <div className="flex items-center gap-5">
            <div className="items-center gap-5 hidden icon-group">
              <div
                role="button"
                tabIndex={0}
                aria-label={`Preview plan ${id}`}
                data-testid={tid(`plan-row-${id}-preview-button`)}
                onClick={() => {
                  setIsPreview(true);
                  setIsPreviewId(id);
                }}
              >
                <EyeIcon
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </div>
            </div>
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label={`Open actions for plan ${id}`}
                data-testid={tid(`plan-row-${id}-actions-button`)}
              >
                <div className="hover:!bg-bgHover p-[9px] rounded-full">
                  <MoreIcon
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items
                className={`absolute ${!isUpdatePaymentMethod && !isDowngradeSchedule ? "w-[153px] -left-[130px]" : "w-[225px] -left-[200px]"} top-[35px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg`}
              >
                <Menu.Item
                  as="div"
                  data-testid={tid(`plan-row-${id}-preview-menu-item`)}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={() => {
                    setIsPreview(true);
                    setIsPreviewId(id);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>

                {row?.last_payment_status?.toLowerCase() !== "cancelled" &&
                  row?.last_payment_status?.toLowerCase() !== "canceled" &&
                  row?.sub_status !== "CANCELLED" &&
                  row?.sub_status !== "CANCELED" &&
                  !isCancelledAtPeriodEnd && (
                    <>
                      {isUpdatePlan && !Boolean(row?.plan_id > 0) &&
                        row?.last_payment_status !== "Completed" &&
                        row?.sub_status !== "ENDED" && (
                          <Menu.Item
                            as="div"
                            data-testid={tid(
                              `plan-row-${id}-update-plan-menu-item`
                            )}
                            className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                            onClick={() => {
                              if (row?.recurring_type === "subscription") {
                                setUpdateSubscribePlan(true);
                                setUpdateSubscribePlanId(
                                  encode(
                                    `${id}_${clusterId}_${new Date().getTime()}`,
                                    "sub_"
                                  )
                                );
                              } else {
                                setIsUpdatePlanOpen(true);
                                setUpdatePlanId(
                                  encode(
                                    `${id}_${clusterId}_${new Date().getTime()}`,
                                    "plan_"
                                  )
                                );
                              }
                            }}
                          >
                            <ReceiptEditIcon /> Update plan
                          </Menu.Item>
                        )}

                      {isUpdatePaymentMethod && !isDowngradeSchedule && !Boolean(row?.plan_id > 0) && (
                        <Menu.Item
                          as="div"
                          data-testid={tid(
                            `plan-row-${id}-update-payment-method-menu-item`
                          )}
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                          onClick={() => {
                            let slug = encode(
                              `${row?.id}_${clusterId}_${new Date().getTime()}`,
                              "sub_"
                            );
                            setUpdateSubscriptionMethodId(slug);
                            setUpdateSubscriptionMethod(true);
                          }}
                        >
                          <PaymentMethodUpdateIcon /> Update payment method
                        </Menu.Item>
                      )}

                      {isDowngradeSchedule && (
                        <Menu.Item
                          as="div"
                          data-testid={tid(
                            `plan-row-${id}-manage-update-menu-item`
                          )}
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setManageUpdatePlanModal({
                              id: row?.id,
                              showModal: true,
                            });
                          }}
                        >
                          <ManageUpdateIcon width="16" height="16" /> Manage
                          scheduled update
                        </Menu.Item>
                      )}
                      {accountPermission?.cancel_plan === SettingsValue.YES &&
                        !isSettlementPlan && !Boolean(row?.plan_id > 0) &&
                        row?.last_payment_status !== "Completed" &&
                        row.sub_status !== "ENDED" &&
                        row.sub_status !== "CANCELLED" && (
                          <Menu.Item
                            as="div"
                            data-testid={tid(
                              `plan-row-${id}-cancel-plan-menu-item`
                            )}
                            className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] border-t border-strokePrimary"
                            onClick={() => {
                              if (row?.recurring_type === "subscription") {
                                setCancelSubscribePlan(true);
                                setCancelSubscribePlanId(id);
                              } else {
                                setCancelPlan({
                                  id: row?.id,
                                  showModal: true,
                                });
                              }
                            }}
                          >
                            <CloseRingIcon width="16" height="16" /> Cancel plan
                          </Menu.Item>
                        )}
                    </>
                  )}

                {isCancelledAtPeriodEnd && (
                  <Menu.Item
                    as="div"
                    data-testid={tid(`plan-row-${id}-dont-cancel-menu-item`)}
                    className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] border-t border-strokePrimary"
                    onClick={() => {
                      setDontCancelPlanModal({ id: row?.id, showModal: true });
                    }}
                  >
                    <CloseRingIcon width="16" height="16" /> Don't cancel
                  </Menu.Item>
                )}
              </Menu.Items>
            </Menu>
          </div>
        );
      },
    },
  ];

  // Effects
  useEffect(() => {
    onGetSubscriptionCount();
  }, []);

  useEffect(() => {
    onGetSubscriptionHistory();
    if (location.state?.status === "active") {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [filter]);

  const selectedSlot = useMemo(() => {
    if (status.length && status?.length === 1) {
      if (status.includes("Active")) {
        return "active";
      } else if (status.includes("Completed")) {
        return "completed";
      } else if (status.includes("Canceled")) {
        return "cancelled";
      }
      return "";
    } else {
      return "all";
    }
  }, [selectedFilter, status]);

  useEffect(() => {
    if (selectedFilter === "all" && !status?.length) return;
    const statusMap: Record<StatusType, string[]> = {
      all: ["all"],
      active: ["Active"],
      completed: ["Completed"],
      cancelled: ["Canceled"],
    };
    setStatus(statusMap[selectedFilter] || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    if (
      searchParams.has("subsDrawer") &&
      searchParams.get("subsDrawer") === "true"
    ) {
      if (searchParams.get("isUpdateSubscriptionPaymentMethod") === "true") {
        setUpdateSubscriptionMethod(true);
        let id = searchParams.get("subscriptionId");
        setUpdateSubscriptionMethodId(
          encode(`${id}_${clusterId}_${new Date().getTime()}`, "sub_")
        );
      } else {
        setUpdateSubscribePlan(true);
        let id = searchParams.get("subscriptionId");
        setUpdateSubscribePlanId(
          encode(`${id}_${clusterId}_${new Date().getTime()}`, "sub_")
        );
      }
    }
    if (
      searchParams.has("plansDrawer") &&
      searchParams.get("plansDrawer") === "true" &&
      searchParams.has("planId")
    ) {
      setIsUpdatePlanOpen(true);
      let id = searchParams.get("planId") ?? "";
      setUpdatePlanId(id);
    }
  }, []);

  useEffect(() => {
    const methodFormatted = method?.map((data) => cardToShortname(data));
    if (!status?.length) setSelectedFilter("all");
    let typeFilter: any = {};

    setFilter(
      dateFilter
        ? {
            skip: 0,
            limit: 20,
            method:
              method.length > 0 ? JSON.stringify(methodFormatted) : undefined,
            status:
              status.length > 0
                ? JSON.stringify(
                    status.map((item: any) =>
                      item === "Canceled" ? "Cancelled" : item
                    )
                  )
                : undefined,
            dateFilter,
            amount: amount ? JSON.stringify(amount) : undefined,
            ...typeFilter,
          }
        : {
            ...filter,
            skip: 0,
            method:
              method.length > 0 ? JSON.stringify(methodFormatted) : undefined,
            status:
              status.length > 0
                ? status.map((item: any) =>
                    item === "Canceled" ? "Cancelled" : item
                  )
                : undefined,
            amount: amount ? JSON.stringify(amount) : undefined,
            ...typeFilter,
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method, amount, dateFilter]);

  // ...existing code...
  if (subscriptionCount?.All === 0) {
    return (
      <div
        className="w-full lg:h-full h-[80%] lg:px-0 px-5"
        data-testid={tid("empty-page")}
      >
        <Header
          headerContent={
            <div className="w-full lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:py-0 py-5">
              Subscriptions & Plans
            </div>
          }
        />

        <div className="text-center lg:pt-[93px] lg:pb-[45px] h-full lg:px-0 px-5 grid place-items-center">
          <div className="h-fit w-fit flex items-center flex-col justify-center gap-10">
            <NoSubscription
              width={window.innerWidth < 500 ? 80 : 100}
              height={window.innerWidth < 500 ? 80 : 100}
            />
            <div>
              <p className="lg:text-2xl text-xl text-txtPrimary font-semibold mb-4">
                No payment plans or subscriptions found
              </p>
              <p className="lg:text-lg text-base text-txtParagraph">
                It’s a bit deserted here in payment plan territory. Ready to set
                up a new plan?
              </p>
            </div>
            <Button
              text="Set up new plan"
              data-testid={tid("empty-set-up-plan-button")}
              aria-label="Set up new payment plan"
              className="lg:!px-11 lg:!py-[10px] !px-4 !py-2"
              onClick={() => navigate(`/${cluster_id}/make-payment`)}
            />
          </div>
        </div>
      </div>
    );
  }

  const GetRow = (dataRow?: any) => {
    let status =
      dataRow?.recurring_type !== "subscription"
        ? dataRow?.last_payment_status
          ? dataRow?.last_payment_status?.toLowerCase()
          : ""
        : dataRow?.sub_status
          ? dataRow?.sub_status?.toLowerCase()
          : "active";
    let { label, Icon, className } = getStatusDetails(status);

    const isAgreement = dataRow?.plan_id && dataRow?.plan_id > 0;

    const filteredProducts = dataRow?.productSubscriptions?.filter(
      (product: any) => product?.priceDetails?.frequency === "recurring"
    );

    const isSettlementPlan = isSettlementOriginPlan(dataRow);

    const isUpdatePlan = isSettlementPlan
      ? false
      : dataRow?.plan_id > 0 || dataRow?.recurring_type === "schedule"
        ? accountPermission?.modify_plan === SettingsValue.YES && !isAgreement
        : filteredProducts?.length === 1 &&
          !dataRow?.downgrade_pending &&
          ["ACTIVE", "PAST_DUE"].includes(dataRow?.sub_status) &&
          dataRow?.payment_collection_type === "MOF";

    const isUpdatePaymentMethod =
      (dataRow?.recurring_type === "subscription" &&
        ["ACTIVE", "PAST_DUE", "UNPAID", "INCOMPLETE"].includes(
          dataRow?.sub_status
        )) ||
      (dataRow?.recurring_type === "schedule" &&
        dataRow?.last_payment_status?.toLowerCase() === "active");

    const isCancelledAtPeriodEnd =
      dataRow?.recurring_type === "subscription" &&
      dataRow?.canceled_at !== null &&
      isCancelsSoon(dataRow?.canceled_at);

    const isDowngradeSchedule =
      dataRow?.recurring_type === "subscription" &&
      dataRow?.downgrade_pending &&
      dataRow?.sub_status !== "CANCELLED" &&
      dataRow?.sub_status !== "CANCELED";

    const date =
      dataRow?.canceled_at && isCancelledAtPeriodEnd
        ? formatPaymentListDate(
            dataRow?.canceled_at,
            userData?.data?.accountData?.timezone,
            "long"
          )
        : isDowngradeSchedule
          ? formatPaymentListDate(
              dataRow?.next_billing_date,
              userData?.data?.accountData?.timezone,
              "long"
            )
          : "";

    const tooltipText =
      (isCancelledAtPeriodEnd
        ? "Your plan will be canceled on "
        : "A plan change is scheduled for ") + date;

    const isReasonAvailable =
      dataRow?.cancelling_reason && dataRow?.cancelling_reason !== "";

    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Preview plan ${dataRow?.id || ""}`}
        data-testid={tid(`plan-mobile-row-${dataRow?.id || "unknown"}`)}
        className="w-full py-3 flex flex-col items-start justify-start gap-[10px] relative"
        onClick={() => {
          setIsPreview(true);
          setIsPreviewId(dataRow?.id);
        }}
      >
        <div className="flex justify-between items-center w-full">
          <span className="text-xl font-medium min-w-[140px] w-full truncate text-txtPrimary">
            {/* {dataRow?.sub_status === "CANCELLED" ||
            dataRow?.sub_status === "ENDED"
              ? "--"
              : dataRow?.next_payment_amount
                ? formatCurrency(
                    dataRow?.next_payment_amount + (dataRow?.cf_amount || 0)
                  ) || "$0.00"
                : "--"} */}
            {dataRow?.plan_id > 0
              ? "Payment Agreement"
              : dataRow?.recurring_type === "schedule"
                ? "Custom Plan"
                : getSubscriptionName(dataRow?.productSubscriptions)}
          </span>
          <span className="flex items-center justify-end gap-2">
            {isReasonAvailable ? (
              <CustomTooltip
                maxwidth="264px"
                place="top"
                id={`status-${dataRow.id}`}
                content={`<div class="font-normal text-xs leading-[18px] w-[150px]" >Reason: ${dataRow.cancelling_reason}. </div>`}
              >
                <div
                  id={`status-${dataRow.id}`}
                  className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md capitalize ${className}`}
                >
                  {Icon}
                  {label}
                </div>
              </CustomTooltip>
            ) : (
              <div
                className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md capitalize ${className}`}
              >
                {Icon}
                {label}
              </div>
            )}

            {dataRow?.showIcon &&
              dataRow?.showIcon !== "false" &&
              label !== "Canceled" && (
                <>
                  <CustomTooltip
                    maxwidth="264px"
                    place="top"
                    id={`Schedule-custom-${dataRow.id}`}
                    content={`<div class="font-normal text-xs leading-[18px] w-[250px]" >  ${dataRow?.recurring_type === "subscription" ? "There are unpaid invoice(s) for this subscription." : "One or more of your recent payments have failed. Click here to view plan and retry."}</div>`}
                    stopPropagation={true}
                  >
                    <div
                      className="w-fit cursor-pointer relative"
                      id={`Schedule-custom-${dataRow.id}`}
                    >
                      <InfoRedCircleIcon />
                    </div>
                  </CustomTooltip>
                </>
              )}
            {(isCancelledAtPeriodEnd || isDowngradeSchedule) && (
              <CustomTooltip
                maxwidth="264px"
                place="top"
                id={`Schedule-calendar-${dataRow?.id}`}
                content={`<div class="font-normal text-xs leading-[18px] w-[180px]" > ${tooltipText}. </div>`}
              >
                <div
                  className="w-fit cursor-pointer relative"
                  id={`Schedule-calendar-${dataRow?.id}`}
                >
                  <CancelledCalendar />
                </div>
              </CustomTooltip>
            )}
            <Menu as="div" className="relative grid place-items-center">
              <Menu.Button
                aria-label={`Open actions for plan ${dataRow?.id || ""}`}
                data-testid={tid(
                  `plan-mobile-row-${dataRow?.id || "unknown"}-actions-button`
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreIcon
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </Menu.Button>

              <Menu.Items
                className={`absolute  ${!isUpdatePaymentMethod && !isDowngradeSchedule ? "w-[153px] -left-[130px]" : "w-[226px] -left-[200px]"} top-[35px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg`}
              >
                <Menu.Item
                  as="div"
                  data-testid={tid(
                    `plan-mobile-row-${dataRow?.id || "unknown"}-preview-menu-item`
                  )}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPreview(true);
                    setIsPreviewId(dataRow?.id);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>

                {dataRow?.last_payment_status?.toLowerCase() !== "cancelled" &&
                  dataRow?.last_payment_status?.toLowerCase() !== "canceled" &&
                  dataRow?.sub_status !== "CANCELLED" && 
                  dataRow?.sub_status !== "CANCELED" &&
                  !isCancelledAtPeriodEnd && (
                    <>
                      {isUpdatePlan && !Boolean(dataRow?.plan_id > 0) &&
                        dataRow?.last_payment_status !== "Completed" &&
                        dataRow?.sub_status !== "ENDED" && (
                          <Menu.Item
                            as="div"
                            data-testid={tid(
                              `plan-mobile-row-${dataRow?.id || "unknown"}-update-plan-menu-item`
                            )}
                            className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (dataRow?.recurring_type === "subscription") {
                                setUpdateSubscribePlan(true);
                                setUpdateSubscribePlanId(
                                  encode(
                                    `${dataRow.id}_${clusterId}_${new Date().getTime()}`,
                                    "sub_"
                                  )
                                );
                              } else {
                                setIsUpdatePlanOpen(true);
                                setUpdatePlanId(
                                  encode(
                                    `${dataRow?.id}_${clusterId}_${new Date().getTime()}`,
                                    "plan_"
                                  )
                                );
                              }
                            }}
                          >
                            <ReceiptEditIcon /> Update plan
                          </Menu.Item>
                        )}

                      {isUpdatePaymentMethod && !isDowngradeSchedule && !Boolean(dataRow?.plan_id > 0) && (
                        <Menu.Item
                          as="div"
                          data-testid={tid(
                            `plan-mobile-row-${dataRow?.id || "unknown"}-update-payment-method-menu-item`
                          )}
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            let slug = encode(
                              `${dataRow?.id}_${clusterId}_${new Date().getTime()}`,
                              "sub_"
                            );
                            setUpdateSubscriptionMethodId(slug);
                            setUpdateSubscriptionMethod(true);
                          }}
                        >
                          <PaymentMethodUpdateIcon /> Update payment method
                        </Menu.Item>
                      )}

                      {isDowngradeSchedule && (
                        <Menu.Item
                          as="div"
                          data-testid={tid(
                            `plan-mobile-row-${dataRow?.id || "unknown"}-manage-update-menu-item`
                          )}
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setManageUpdatePlanModal({
                              id: dataRow?.id,
                              showModal: true,
                            });
                          }}
                        >
                          <ManageUpdateIcon width="16" height="16" /> Manage
                          scheduled update
                        </Menu.Item>
                      )}

                      {accountPermission?.cancel_plan === SettingsValue.YES &&
                        !isSettlementPlan && !Boolean(dataRow?.plan_id > 0) &&
                        dataRow?.last_payment_status !== "Completed" &&
                        dataRow.sub_status !== "ENDED" &&
                        dataRow.sub_status !== "CANCELLED" && (
                          <Menu.Item
                            as="div"
                            data-testid={tid(
                              `plan-mobile-row-${dataRow?.id || "unknown"}-cancel-plan-menu-item`
                            )}
                            className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] border-t border-strokePrimary"
                            onClick={(e: any) => {
                              e.stopPropagation();
                              if (dataRow?.recurring_type === "subscription") {
                                setCancelSubscribePlan(true);
                                setCancelSubscribePlanId(dataRow?.id);
                              } else {
                                setCancelPlan({
                                  id: dataRow?.id,
                                  showModal: true,
                                });
                              }
                            }}
                          >
                            <CloseRingIcon width="16" height="16" /> Cancel plan
                          </Menu.Item>
                        )}
                    </>
                  )}

                {isCancelledAtPeriodEnd && (
                  <Menu.Item
                    as="div"
                    data-testid={tid(
                      `plan-mobile-row-${dataRow?.id || "unknown"}-dont-cancel-menu-item`
                    )}
                    className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] border-t border-strokePrimary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDontCancelPlanModal({
                        id: dataRow?.id,
                        showModal: true,
                      });
                    }}
                  >
                    <CloseRingIcon width="16" height="16" /> Don't cancel
                  </Menu.Item>
                )}
              </Menu.Items>
            </Menu>
          </span>
        </div>
        <div className="w-[85%] flex flex-col gap-[10px]">
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Payment method</span>
            <span className="w-[50%]">
              {getPaymentMethod({
                card_type: dataRow?.cc_type,
                method: dataRow?.payment_method,
                digits: dataRow?.cc_4digit,
                account_number: dataRow?.cc_4digit,
                className: "",
                isHidden: false,
                bank_name: dataRow?.bank_name,
                rowId: dataRow?.id,
                payment_collection_type:
                  dataRow?.recurring_type === "schedule" || dataRow?.plan_id > 0
                    ? "MOF"
                    : dataRow?.payment_collection_type,
              }) || "--"}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Created date</span>
            <span className="w-[50%] text-txtPrimary">
              {formatPaymentListDate(
                dataRow.updation_date,
                userData?.data?.accountData?.timezone,
                "short"
              )}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Next payment amount</span>
            <span className="w-[50%] text-txtPrimary">
              {dataRow?.sub_status === "CANCELLED" ||
              dataRow?.sub_status === "ENDED" ||
              dataRow?.last_payment_status === "Cancelled" ||
              dataRow?.last_payment_status === "Completed"
                ? "--"
                : dataRow?.next_payment_amount
                  ? formatCurrency(
                      dataRow?.next_payment_amount + (dataRow?.cf_amount || 0)
                    ) || "$0.00"
                  : "--"}
              {/* {dataRow?.plan_id > 0
                ? "Payment Agreement"
                : dataRow?.recurring_type === "schedule"
                  ? "Custom Plan"
                  : getSubscriptionName(dataRow?.productSubscriptions)} */}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Frequency</span>
            <span className="w-[50%] text-txtPrimary">
              {changePaymentScheduleName(
                dataRow?.payment_schedule,
                dataRow?.productSubscriptions,
                dataRow?.recurring_type
              )}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Next payment</span>
            <span className="w-[50%] text-txtPrimary">
              {dataRow?.sub_status === "CANCELLED" ||
              dataRow?.sub_status === "ENDED" ||
              dataRow?.last_payment_status === "Cancelled" ||
              dataRow?.last_payment_status === "Completed"
                ? "--"
                : dataRow?.next_billing_date
                  ? formatPaymentListDate(
                      dataRow.next_billing_date,
                      userData?.data?.accountData?.timezone,
                      "short"
                    )
                  : "--"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="w-full lg:h-full lg:block flex flex-col lg:overflow-hidden"
        data-testid={tid("page")}
      >
        <Header
          headerContent={
            <div className="w-full lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:p-0 p-5 h-fit">
              Subscriptions & Plans
            </div>
          }
        />
        <div className="lg:pt-[93px] h-full lg:overflow-y-auto">
          <div className="lg:px-10 lg:pt-10 h-full lg:overflow-y-auto">
            <div
              className="lg:px-0 px-5 flex items-center lg:gap-[28px] lg:flex-nowrap flex-wrap gap-5 lg:overflow-x-auto"
              data-testid={tid("summary-filters")}
            >
              <div
                role="button"
                tabIndex={0}
                aria-pressed={selectedSlot === "all"}
                aria-label="Show all subscriptions and plans"
                data-testid={tid("summary-filter-all")}
                className={`${
                  selectedSlot === "all"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                onClick={() => {
                  setSelectedFilter("all");
                  setStatus([]);
                }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  All
                </div>
                <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.All || 0
                  )}
                </div>
              </div>
              <div
                role="button"
                tabIndex={0}
                aria-pressed={selectedSlot === "active"}
                aria-label="Show active subscriptions and plans"
                data-testid={tid("summary-filter-active")}
                className={`${
                  selectedSlot === "active"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                onClick={() => {
                  setSelectedFilter("active");
                  // setStatus([]);
                }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  Active
                </div>
                <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.Active || 0
                  )}
                </div>
              </div>
              <div
                role="button"
                tabIndex={0}
                aria-pressed={selectedSlot === "completed"}
                aria-label="Show completed subscriptions and plans"
                data-testid={tid("summary-filter-completed")}
                className={`${
                  selectedSlot === "completed"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                onClick={() => {
                  setSelectedFilter("completed");
                }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  Completed
                </div>
                <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.Completed || 0
                  )}
                </div>
              </div>
              <div
                role="button"
                tabIndex={0}
                aria-pressed={selectedSlot === "cancelled"}
                aria-label="Show canceled subscriptions and plans"
                data-testid={tid("summary-filter-canceled")}
                className={`${
                  selectedSlot === "cancelled"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                onClick={() => {
                  setSelectedFilter("cancelled");
                }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  Canceled
                </div>
                <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.Cancelled || 0
                  )}
                </div>
              </div>
            </div>
            <div
              className="lg:px-0 px-5 pt-[30px] flex items-center gap-3 overflow-x-scroll scrollbar-hide"
              ref={filterScrollRef}
              data-testid={tid("filter-bar")}
            >
              <div className="w-fit" ref={dateFilterRef}>
                <DateFilter
                  options={FilterDateOptions}
                  dateInput1={dateInput1}
                  dateInput2={dateInput2}
                  setDateInput1={setDateInput1}
                  setDateInput2={setDateInput2}
                  label="Date"
                  setdateFilter={setdateFilter}
                  onOpen={() => {
                    if (filterScrollRef.current && dateFilterRef.current) {
                      filterScrollRef.current.scrollTo({
                        left: 0,
                        behavior: "smooth",
                      });
                    }
                  }}
                />
              </div>
              <div className="w-fit" ref={amountFilterRef}>
                <AmountFilter
                  options={FilterAmountOptions}
                  amount={amount}
                  setAmount={setAmount}
                  label="Amount"
                  onOpen={() => {
                    if (filterScrollRef.current && amountFilterRef.current) {
                      filterScrollRef.current.scrollTo({
                        left:
                          amountFilterRef.current.offsetLeft -
                          (filterScrollRef.current.offsetLeft || 0),
                        behavior: "smooth",
                      });
                    }
                  }}
                />
              </div>
              <div className="w-fit" ref={statusFilterRef}>
                <SelectAllDropDown
                  options={SubscriptionsStatusOptions}
                  checked={status}
                  setChecked={setStatus}
                  label="Status"
                  onOpen={() => {
                    if (filterScrollRef.current && statusFilterRef.current) {
                      filterScrollRef.current.scrollTo({
                        left:
                          statusFilterRef.current.offsetLeft -
                          (filterScrollRef.current.offsetLeft || 0),
                        behavior: "smooth",
                      });
                    }
                  }}
                />
              </div>
              <div className="w-fit" ref={methodFilterRef}>
                <SelectAllDropDown
                  options={PaymentFilterMethodOptions}
                  checked={method}
                  setChecked={setMethod}
                  label="Payment Method"
                  onOpen={() => {
                    if (filterScrollRef.current && methodFilterRef.current) {
                      filterScrollRef.current.scrollTo({
                        left:
                          methodFilterRef.current.offsetLeft -
                          (filterScrollRef.current.offsetLeft || 0),
                        behavior: "smooth",
                      });
                    }
                  }}
                />
              </div>
            </div>
            <div className="pt-[30px]">
              <CustomTable
                data-testid={tid("plans-table")}
                ariaLabel="Subscriptions and plans table"
                getRowKey={(row: SubscriptionPlansRow, index: number) =>
                  row?.id || index
                }
                getRowTestId={(row: SubscriptionPlansRow, index: number) =>
                  tid(`plan-mobile-row-wrapper-${row?.id || index}`)
                }
                columns={columns}
                data={subscriptionHistory?.paymentPlanHistoryData || []}
                fixedHeaderScrollHeight="calc(var(--shell-vh) - 452px)"
                perPage={filter?.limit}
                currentPage={(filter?.skip + 20) / 20}
                totalCount={subscriptionHistory?.totalCount || 0}
                isTableLoading={isTableLoading}
                onNextClick={(e) => {
                  setFilter({
                    ...filter,
                    skip: filter?.skip + 20,
                  });
                }}
                onPreviousClick={(e) => {
                  setFilter({
                    ...filter,
                    skip: filter?.skip - 20,
                  });
                }}
                GetRow={GetRow}
                onRowClick={(row: any, e: any) => {
                  setIsPreview(true);
                  setIsPreviewId(row?.id);
                }}
              />
            </div>
            <div>
              <Drawer
                enableOverlay={true}
                lockBackgroundScroll={true}
                direction="right"
                open={isPreview}
                size={isLessThan590 ? `100vw` : "590px"}
                onClose={() => {
                  if (isNestedPreviewOpen) return;
                  setIsPreview(false);
                  setIsPreviewId("");
                }}
                overlayColor="#000"
                zIndex={999}
              >
                <div
                  className="contents"
                  data-testid={tid("subscription-preview-drawer")}
                >
                  <SubscriptionsPreview
                    id={Number(isPreviewId)}
                    setId={setIsPreviewId}
                    show={isPreview}
                    setShow={setIsPreview}
                    setHandleDrawerSize={setHandleDrawerSize}
                    onNestedPreviewChange={setIsNestedPreviewOpen}
                  />
                </div>
              </Drawer>
            </div>
          </div>
        </div>
      </div>
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isUpdatePreview}
          size={isLessThan590 ? `100vw` : "590px"}
          onClose={() => {
            setIsUpdatePreview(false);
            setHandleDrawerSize(0);
          }}
          overlayColor="#000"
        >
          <div
            className="contents"
            data-testid={tid("plan-update-preview-drawer")}
          >
            <PlanPreview
              show={isUpdatePreview}
              setShow={setIsUpdatePreview}
              schedule={updateData?.paymentPlan}
              isLoading={isLoading}
              scheduleMethod={selectedMethod}
              setHandleDrawerSize={setHandleDrawerSize}
            />
          </div>
        </Drawer>
      </div>

      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={updateSubscriptionMethod}
          size={`${handleDrawerSize ? "0" : isLessThan680 ? `100vw` : "680px"}`}
          onClose={() => {
            setUpdateSubscriptionMethod(false);
            setUpdateSubscriptionMethodId(null);
          }}
          overlayColor="#000"
        >
          <div
            className="contents"
            data-testid={tid("update-payment-method-drawer")}
          >
            <UpdatePaymentMethod
              id={updateSubscriptionMethodId}
              show={updateSubscriptionMethod}
              setShow={setUpdateSubscriptionMethod}
              setId={setUpdateSubscriptionMethodId}
              fetchDetails={() => {
                onGetSubscriptionHistory();
                onGetSubscriptionCount();
              }}
              setHandleDrawerSize={setHandleDrawerSize}
              setIsPreview={setIsPreview}
              setIsPreviewId={setIsPreviewId}
              // errrorMessage={errrorMessage}
              // setErrorMessage={setErrorMessage}
            />
          </div>
        </Drawer>
      </div>
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isUpdatePlanOpen}
          size={`${handleDrawerSize ? "0" : isLessThan920 ? `100vw` : "920px"}`}
          onClose={() => {
            setIsUpdatePreview(false);
            setUpdatePlanId("");
          }}
          overlayColor="#000"
        >
          <div className="contents" data-testid={tid("update-plan-drawer")}>
            <UpdatePaymentPlan
              id={updatePlanId}
              show={isUpdatePlanOpen}
              setShow={setIsUpdatePlanOpen}
              setUpdatePlanId={(value?: string) => {
                setUpdatePlanId(value || "");
              }}
              setIsPreview={setIsUpdatePreview}
              setHandleDrawerSize={setHandleDrawerSize}
              fetchDetails={() => {
                onGetSubscriptionHistory();
                onGetSubscriptionCount();
              }}
              setSelectedMethod={setSelectedMethod}
              setUpdateData={setUpdateData}
              isApiCall={isApiCall}
              setIsApiCall={setIsApiCall}
              setFinalPreview={setIsPreview}
              setFinalPreviewId={(value?: string) => {
                setIsPreviewId(value || "");
              }}
              isFinalPreview={isPreview}
            />
          </div>
        </Drawer>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={updateSubscribePlan}
          size={`${handleDrawerSize ? "0" : isLessThan680 ? `100vw` : "680px"}`}
          onClose={() => {
            setUpdateSubscribePlan(false);
            setUpdateSubscribePlanId(null);
            setHandleDrawerSize(0);
          }}
          overlayColor="#000"
        >
          <div
            className="contents"
            data-testid={tid("update-subscription-plan-drawer")}
          >
            <UpdateSusbcribePlan
              id={updateSubscribePlanId}
              show={updateSubscribePlan}
              setShow={setUpdateSubscribePlan}
              setId={setUpdateSubscribePlanId}
              fetchDetails={() => {
                onGetSubscriptionHistory();
                onGetSubscriptionCount();
              }}
              setHandleDrawerSize={setHandleDrawerSize}
              setIsPreview={setIsPreview}
              setIsPreviewId={setIsPreviewId}
            />
          </div>
        </Drawer>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={cancelSubscribePlan}
          size={`${handleDrawerSize ? "0" : isLessThan680 ? `100vw` : "680px"}`}
          onClose={() => {
            setCancelSubscribePlan(false);
            setCancelSubscribePlanId(null);
          }}
          overlayColor="#000"
        >
          <div
            className="contents"
            data-testid={tid("cancel-subscription-plan-drawer")}
          >
            <CancelSubscribePlan
              id={cancelSubscribePlanId}
              show={cancelSubscribePlan}
              setShow={setCancelSubscribePlan}
              setId={setCancelSubscribePlanId}
              fetchDetails={() => {
                onGetSubscriptionHistory();
                onGetSubscriptionCount();
              }}
              setHandleDrawerSize={setHandleDrawerSize}
              setIsPreview={setIsPreview}
              setIsPreviewId={setIsPreviewId}
            />
          </div>
        </Drawer>
      </div>
      <CustomModal
        signModal={false}
        isLoading={cancelBtnLoading}
        isOpen={cancelPlan?.showModal}
        title="Cancel Plan"
        doneText="Cancel Plan"
        data-testid={tid("cancel-plan-modal")}
        aria-label="Cancel plan confirmation"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        cancleText="Not Now"
        onDoneClick={() => {
          handleCancelPaymentPlan();
        }}
        onCloseClick={() => {
          setCancelPlan({
            id: null,
            showModal: false,
          });
        }}
        children={
          <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
            Are you sure you want to proceed? Cancellation is permanent and
            cannot be undone.
          </div>
        }
      />

      <CustomModal
        signModal={false}
        isLoading={isDontCancelLoading}
        isOpen={dontCancelPlanModal?.showModal}
        title="Alert"
        doneText="OK"
        data-testid={tid("dont-cancel-plan-modal")}
        aria-label="Keep subscription active confirmation"
        className="max-w-[600px] w-full px-5 h-fit rounded-[10px]"
        cancleText="Cancel"
        onDoneClick={() => {
          handleDontCancelPaymentPlan();
        }}
        onCloseClick={() => {
          setDontCancelPlanModal({
            id: null,
            showModal: false,
          });
        }}
        children={
          <div className="pt-5 w-full  lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
            This will undo the scheduled cancelation and keep your subscription
            active beyond the current billing cycle.
            <br />
            <br />
            Are you sure you want to continue the subscription?
          </div>
        }
      />

      <CustomModal
        signModal={true}
        isOpen={manageUpdatePlanModal?.showModal}
        title="Alert"
        doneText="OK"
        data-testid={tid("manage-scheduled-update-modal")}
        aria-label="Manage scheduled update"
        className="max-w-[600px] w-full px-5 h-fit rounded-[10px]"
        cancleText="Cancel"
        onDoneClick={() => {
          // handleDontCancelPaymentPlan();
        }}
        onCloseClick={() => {
          setManageUpdatePlanModal({
            id: null,
            showModal: false,
          });
        }}
      >
        <div data-testid={tid("manage-scheduled-update-content")}>
          <ScheduleUpdatePlan
            id={manageUpdatePlanModal?.id || null}
            setShowModal={setManageUpdatePlanModal}
            show={manageUpdatePlanModal?.showModal}
            setMsg={setMsg}
            fetchDetails={() => {
              onGetSubscriptionHistory();
              onGetSubscriptionCount();
            }}
          />
        </div>
      </CustomModal>

      <MessageToast
        variant={msg?.variant}
        content={msg?.content}
        isProgressBar
        width="w-full max-w-[810px] px-5"
        className=" !absolute !right-1/2 !translate-x-1/2"
        onCloseClick={() => {
          setMsg({
            ...msg,
            variant: "",
            content: null,
          });
        }}
      />
    </>
  );
};

const SubscriptionsAndPlans = () => (
  <TestIdScope scope="subscriptions-plans">
    <SubscriptionsAndPlansContent />
  </TestIdScope>
);

export default SubscriptionsAndPlans;
